import React, { useState, createContext, useContext, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import DashboardPage from './pages/DashboardPage.js';
import LoginPage from './pages/LoginPage.js';
import ProductsPage from './pages/ProductsPage.js';
import CategoriesPage from './pages/CategoriesPage.js';
import OrdersPage from './pages/OrdersPage.js';
import ClientsPage from './pages/ClientsPage.js';
import SuppliersPage from './pages/SuppliersPage.js';
import DeliveryPage from './pages/DeliveryPage.js';
import InventoryPage from './pages/InventoryPage.js';
import AnalyticsPage from './pages/AnalyticsPage.js';
import SupplierDashboard from './pages/SupplierDashboard.js';
import Sidebar from './components/Sidebar.js';
import SupplierSidebar from './components/SupplierSidebar.js';
import DeliverySidebar from './components/DeliverySidebar.js';
import SupplierProductsPage from './pages/supplier/ProductsPage.js'; 
import SupplierOrdersPage from './pages/supplier/OrdersPage.js';
import SupplierDeliveryPage from './pages/supplier/DeliveryPage.js';
import SupplierInventoryPage from './pages/supplier/InventoryPage.js';
import SupplierAnalyticsPage from './pages/supplier/AnalyticsPage.js';
import DeliveryDashboard from './pages/DeliveryDashboard.js';
import MyDeliveryPage from './pages/delivery/DeliveryPage.js';

export const UserContext = createContext(null);

const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setLoading(false);
  };

  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    if (storedAccessToken) {
      try {
        const decodedToken = jwtDecode(storedAccessToken);
        setUser(decodedToken);
      } catch (e) {
        console.error("Token d'accès invalide, déconnexion...");
        logout();
      }
    }
    setLoading(false);
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, logout, loading }}>
      {children}
    </UserContext.Provider>
  );
};

// Protects routes for Admin users
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useContext(UserContext);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-2xl text-gray-700 animate-pulse">Chargement...</div>
      </div>
    );
  }

  if (!user || user.userType !== 'admin') {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// New component to protect routes for Supplier users
const SupplierRoute = ({ children }) => {
  const { user, loading } = useContext(UserContext);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-2xl text-gray-700 animate-pulse">Chargement...</div>
      </div>
    );
  }

  if (!user || user.userType !== 'supplier') {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const DeliveryRoute = ({ children }) => {
  const { user, loading } = useContext(UserContext);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-2xl text-gray-700 animate-pulse">Chargement...</div>
      </div>
    );
  }

  if (!user || user.userType !== 'delivery') {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AppContent = () => {
  const { user } = useContext(UserContext);

  return (
    <div className="flex bg-gray-100 min-h-screen font-sans antialiased">
      {user && user.userType === 'admin' && <Sidebar />}
      {user && user.userType === 'supplier' && <SupplierSidebar />}
      {user && user.userType === 'delivery' && <DeliverySidebar />}
      <main className="flex-1 p-8">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Admin routes, protected for 'admin' users */}
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
          <Route path="/delivery" element={<ProtectedRoute><DeliveryPage /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />

          {/* Supplier dashboard route, protected for 'supplier' users */}
          <Route path="/supplier-dashboard" element={<SupplierRoute><SupplierDashboard /></SupplierRoute>} />
          <Route path="/supplier/products" element={<SupplierRoute><SupplierProductsPage /></SupplierRoute>} />
          <Route path="/supplier/orders" element={<SupplierRoute><SupplierOrdersPage /></SupplierRoute>} />
          <Route path="/supplier/delivery" element={<SupplierRoute><SupplierDeliveryPage /></SupplierRoute>} />
          <Route path="/supplier/inventory" element={<SupplierRoute><SupplierInventoryPage /></SupplierRoute>} />
          <Route path="/supplier/analytics" element={<SupplierRoute><SupplierAnalyticsPage /></SupplierRoute>} />

          {/* Delivery dashboard route, protected for 'delivery' users */}
          <Route path="/delivery-dashboard" element={<DeliveryRoute><DeliveryDashboard /></DeliveryRoute>} />
          <Route path="/delivery/delivery" element={<DeliveryRoute><MyDeliveryPage /></DeliveryRoute>} />
          <Route path="*" element={<h1 className="text-center text-4xl font-bold mt-10">404 - Page introuvable</h1>} />
        </Routes>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </BrowserRouter>
  );
};

export default App;